import React, { useEffect, useRef, useState } from 'react';
import { DropdownWrapper, Wrapper, ButtonClose, Container } from './Dropdown.styles';
import Icon from '../Icon';

interface DropdownProps {
    currentSelected?: number;
    arraySelect?: { label: string; value: number }[];
    colorSelect: string;
    isDisabled: boolean;
    nullMessage?: string;
    messageTag?: string;
    onChangeSelect?: (value: number) => void;
    clearSelect?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
    currentSelected,
    nullMessage,
    arraySelect,
    colorSelect,
    messageTag,
    isDisabled,
    onChangeSelect,
    clearSelect,
}) => {
    const [selectedOption, setSelectedOption] = useState({
        label: (arraySelect && arraySelect[currentSelected - 1]?.label) || nullMessage,
        value: (arraySelect && arraySelect[currentSelected - 1]?.value) || null,
    });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const iconClassName = `icon ${isDropdownOpen ? 'icon-rotate open' : 'icon-rotate'}`;
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const currentColor = selectedOption.label === nullMessage ? 'blank' : colorSelect;

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const buttonClicked = buttonRef.current?.contains(event.target as Node);

            if (!buttonClicked && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(!isDropdownOpen);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mouseup', handleOutsideClick);
        } else {
            document.removeEventListener('mouseup', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mouseup', handleOutsideClick);
        };
    }, [isDropdownOpen]);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleStatusChange = (index: number, optionValue: number) => {
        if (optionValue !== selectedOption.value) {
            setSelectedOption({
                label: arraySelect[index].label,
                value: arraySelect[index].value,
            });

            onChangeSelect?.(optionValue);
        }
        setIsDropdownOpen(false);
    };

    const handleClearSelect = () => {
        setSelectedOption({
            label: nullMessage,
            value: null,
        });
        onChangeSelect?.(null);
    };

    return (
        <Container>
            <Wrapper
                isDisabled={isDisabled || arraySelect.length === 0}
                className={currentColor}
                ref={buttonRef}
                onClick={toggleDropdown}
                data-testid="toggle-dropdown"
            >
                <p className={`label ${currentColor}`}>{arraySelect ? selectedOption.label : messageTag}</p>
                {!isDisabled && arraySelect.length > 0 && (
                    <Icon className={`${iconClassName} ${currentColor}`} disableFill size={12} icon="pcom-icon-chevron-down" />
                )}
            </Wrapper>
            {clearSelect && selectedOption.label !== nullMessage ? (
                <ButtonClose onClick={handleClearSelect} data-testid="btn-clear-select">
                    <Icon className="pcom-icon-close" icon="pcom-icon-circle-close-outline" size={20} />
                </ButtonClose>
            ) : null}
            {isDropdownOpen && !isDisabled && arraySelect.length > 0 && (
                <DropdownWrapper ref={dropdownRef}>
                    <div className="dropdown">
                        {arraySelect.map((item, index) => (
                            <div className="dropdown-item" key={index} onClick={() => handleStatusChange(index, item.value)}>
                                <Wrapper>
                                    <p className="label">{item.label}</p>
                                </Wrapper>
                            </div>
                        ))}
                    </div>
                </DropdownWrapper>
            )}
        </Container>
    );
};

export default React.memo(Dropdown);
